/**
 * Utilidades para tours - funciones reutilizables
 * Centraliza la lógica común de navegación, eventos y esperas
 */

import router from '@/router'

/**
 * Crea una espera asíncrona
 * @param milliseconds - Tiempo de espera en milisegundos
 */
export const wait = (milliseconds: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, milliseconds))
}

/**
 * Navega a una ruta y espera un tiempo determinado
 * @param routeName - Nombre de la ruta a navegar
 * @param waitTime - Tiempo de espera después de la navegación (por defecto 1000ms)
 */
export const navigateAndWait = async (routeName: string, waitTime = 1000): Promise<void> => {
  await router.push({ name: routeName })
  await wait(waitTime)
}

/**
 * Despacha un evento personalizado
 * @param eventName - Nombre del evento
 * @param detail - Datos adicionales del evento (opcional)
 */
export const dispatchCustomEvent = (eventName: string, detail: any = null): void => {
  const eventOptions: CustomEventInit = { bubbles: true }
  if (detail !== null) {
    eventOptions.detail = detail
  }

  const event = new CustomEvent(eventName, eventOptions)
  document.dispatchEvent(event)
}

/**
 * Dispara un evento personalizado
 * @param eventName - Nombre del evento
 * @param detail - Datos adicionales del evento (opcional)
 */
export const customEvent = (eventName: string, detail: any = null): void => {
  const eventOptions: CustomEventInit = { bubbles: true }
  if (detail !== null) {
    eventOptions.detail = detail
  }
  document.dispatchEvent(new CustomEvent(eventName, eventOptions))
}
/**
 * Alterna el estado del mini drawer
 */
export const toggleMiniDrawer = (permanent = false): void => {
  dispatchCustomEvent('is-mini-drawer', { permanent })
}

/**
 * Abre el diálogo de tickets
 */
export const openTicketDialog = (): void => {
  dispatchCustomEvent('open-ticket-dialog')
}

/**
 * Cierra el diálogo de tickets
 */
export const closeTicketDialog = (): void => {
  dispatchCustomEvent('close-ticket-dialog')
}

/**
 * Finaliza el tour de bienvenida
 * @param skipped - Indica si el tour fue omitido
 */
export const finishWelcomeTour = (skipped = false): void => {
  dispatchCustomEvent('finish-welcome-tour', { skipped })
}

/**
 * Fuerza el scroll al inicio de la página usando múltiples métodos
 */
export const forceScrollToTop = (): number => {
  // Cancelar cualquier scroll activo
  try {
    window.stop()
  } catch (e) {
    console.error('No se pudo cancelar scrolls activos')
  }

  // Forzar posición 0 usando múltiples métodos
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0

  // También intentar con scroll nativo
  window.scroll(0, 0)

  // Como último recurso, intentar con scrollIntoView
  document.body.scrollIntoView({ block: 'start', inline: 'start' })

  return window.scrollY
}

/**
 * Realiza scroll suave a un elemento específico
 * @param selector - Selector CSS del elemento
 * @param behavior - Comportamiento del scroll (por defecto 'smooth')
 * @param block - Posición del bloque (por defecto 'start')
 */
export const scrollToElement = (
  selector: string,
  behavior: ScrollBehavior = 'smooth',
  block: ScrollLogicalPosition = 'start'
): void => {
  const element = document.querySelector(selector)
  if (element) {
    element.scrollIntoView({ behavior, block })
  } else {
    console.error(`Elemento ${selector} no encontrado`)
  }
}

/**
 * Busca el primer elemento disponible de una lista de selectores
 * @param selectors - Array de selectores CSS
 * @returns El primer elemento encontrado o null
 */
export const findFirstAvailableElement = (selectors: string[]): Element | null => {
  for (const selector of selectors) {
    const element = document.querySelector(selector)
    if (element) {
      return element
    }
  }
  return null
}

/**
 * Ejecuta scroll agresivo al inicio con múltiples intentos
 * @param attempts - Número de intentos (por defecto 3)
 * @param delayBetweenAttempts - Delay entre intentos en ms (por defecto 500)
 */
export const aggressiveScrollToTop = async (
  attempts = 3,
  delayBetweenAttempts = 500
): Promise<void> => {
  for (let i = 0; i < attempts; i++) {
    forceScrollToTop()
    if (i < attempts - 1) {
      await wait(delayBetweenAttempts)
    }
  }
}
